import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import SmallDoughnut from '@/common/components/SmallDoughnut';
import ThemeTitleIcon from '@/common/components/ThemeTitleIcon';
import { PILLAR_QUESTION_MAP } from '@/common/constants/pillars';
import {
  scoreToColor,
  SCORE_LOW_COLOR,
  SCORE_MEDIUM_COLOR,
  SCORE_HIGH_COLOR,
  SCORE_LOW_THRESHOLD,
  SCORE_MEDIUM_THRESHOLD,
} from '@/areas/dashboard/components/DashboardTeam.utils';
import {
  getPillars,
} from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import { MobilePageContent, MobilePageSection, Title } from '@/mobile/layouts/common/MobilePageStyles';
import { Icon } from 'semantic-ui-react';

interface MobilePillarPageProps {}

const MobilePillarPage: React.FC<MobilePillarPageProps> = () => {
  const { pillarKey } = useParams<{ pillarKey: string }>();
  const pillars = useSelector(getPillars);
  const pillar = pillarKey ? pillars.find((p) => p.key === pillarKey) : undefined;

  const groupedThemes = React.useMemo(() => {
    const themeItems = (Array.isArray(pillar?.themes) ? pillar.themes : [])
      .map((theme) => {
        const signalPct = typeof theme.signal_strength === 'number'
          ? Math.max(0, Math.min(100, theme.signal_strength))
          : Math.round(Math.max(0, Math.min(1, Number(theme.sentiment ?? 0))) * 100);
        const sentimentScore = signalPct / 10;
        const category = sentimentScore >= SCORE_MEDIUM_THRESHOLD
          ? 'positive'
          : sentimentScore >= SCORE_LOW_THRESHOLD
            ? 'mixed'
            : 'negative';

        return {
          title: theme.label || 'Untitled theme',
          description: typeof theme.description === 'string' ? theme.description.trim() : '',
          count: typeof theme.count === 'number' ? theme.count : undefined,
          quotes: Array.isArray((theme as any).representative_quotes)
            ? ((theme as any).representative_quotes as string[])
            : [],
          category,
          statusColor: scoreToColor(sentimentScore),
          sentimentScore,
        };
      })
      .sort((a, b) => b.sentimentScore - a.sentimentScore);

    return {
      positive: themeItems.filter((item) => item.category === 'positive'),
      mixed: themeItems.filter((item) => item.category === 'mixed'),
      negative: themeItems.filter((item) => item.category === 'negative'),
    };
  }, [pillar?.themes]);

  if (!pillar) {
    return <MobilePageContent />;
  }

  const delta = pillar.org_avg !== undefined ? Number((pillar.score - pillar.org_avg).toFixed(1)) : null;

  const sentimentScoreValue = typeof pillar.sentiment_score === 'number'
    ? Math.max(0, Math.min(10, pillar.sentiment_score))
    : Math.round(Math.max(0, Math.min(1, pillar.qual_priority ?? 0)) * 10);

  const sentimentPill = (() => {
    if (sentimentScoreValue >= 8) {
      return {
        label: 'Feedback was Very Positive',
        background: '#EEF6EF',
        color: '#1F6E3D',
        borderColor: '#D2E5D1',
      };
    }
    if (sentimentScoreValue >= 6) {
      return {
        label: 'Feedback was Positive',
        background: '#EAF0FF',
        color: '#1E40AF',
        borderColor: '#C8D7FF',
      };
    }
    if (sentimentScoreValue >= 4) {
      return {
        label: 'Feedback was Mixed',
        background: '#FEF5D6',
        color: '#7C4D1E',
        borderColor: '#F7E4A6',
      };
    }
    if (sentimentScoreValue >= 2) {
      return {
        label: 'Feedback was Mostly Negative',
        background: '#FEE2E2',
        color: '#B91C1C',
        borderColor: '#FECACA',
      };
    }
    return {
      label: 'Feedback was Strong Need for Improvement',
      background: '#FEE2E2',
      color: '#B91C1C',
      borderColor: '#FECACA',
    };
  })();

  return (
    <MobilePageContent>
      <HeaderTop>
        <ScoreCircle>
          <SmallDoughnut value={pillar.score} colorized size={52} />
        </ScoreCircle>
        <HeaderText>
          <Title>{pillar.name}</Title>
          <SubText>Team {pillar.score.toFixed(1)} • Org {pillar.org_avg.toFixed(1)}</SubText>
          <QuestionText>{PILLAR_QUESTION_MAP[pillar.name] || ''}</QuestionText>
        </HeaderText>
        {delta !== null && <DeltaPill delta={delta}>{delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1)}</DeltaPill>}
      </HeaderTop>

      <MobilePageSection padbottom='0px' padtop='0px'>
        <SectionGroup>
          <SectionHeading>Team Feedback</SectionHeading>
          <SentimentPill
            $background={sentimentPill.background}
            $color={sentimentPill.color}
            $borderColor={sentimentPill.borderColor}
          >
            {sentimentPill.label}
          </SentimentPill>

          <ThemeColumn>
            <ThemeColumnHeader $color={SCORE_HIGH_COLOR}>
              <ThemeTitleIcon variant="positive">
                <Icon name="thumbs up" />
              </ThemeTitleIcon>
              Positive Feedback
            </ThemeColumnHeader>
            {groupedThemes.positive.length > 0 ? groupedThemes.positive.map((item) => (
              <ThemeRow key={`positive-${item.title}`}>
                <ThemeHeader>
                  <ThemeStatusBar $color={item.statusColor} />
                  <ThemeTitleText>
                    {item.title}
                    {typeof item.count === 'number' ? <ThemeCount>· {item.count} mentions</ThemeCount> : null}
                  </ThemeTitleText>
                </ThemeHeader>
                <ThemeDescription>
                  {item.description || <EmptyDescription>No description available</EmptyDescription>}
                </ThemeDescription>
                {item.quotes.length > 0 && (
                  <QuoteList>
                    {item.quotes.slice(0, 2).map((quote, idx) => (
                      <QuoteItem key={idx}>&quot;{quote}&quot;</QuoteItem>
                    ))}
                  </QuoteList>
                )}
              </ThemeRow>
            )) : (
              <EmptyCategory>No themes in this category.</EmptyCategory>
            )}
          </ThemeColumn>

          <ThemeColumn>
            <ThemeColumnHeader $color={SCORE_MEDIUM_COLOR}>
              <ThemeTitleIcon variant="neutral">
                <Icon name="comment alternate" />
              </ThemeTitleIcon>
              Mixed Feelings
            </ThemeColumnHeader>
            {groupedThemes.mixed.length > 0 ? groupedThemes.mixed.map((item) => (
              <ThemeRow key={`mixed-${item.title}`}>
                <ThemeHeader>
                  <ThemeStatusBar $color={item.statusColor} />
                  <ThemeTitleText>
                    {item.title}
                    {typeof item.count === 'number' ? <ThemeCount>· {item.count} mentions</ThemeCount> : null}
                  </ThemeTitleText>
                </ThemeHeader>
                <ThemeDescription>
                  {item.description || <EmptyDescription>No description available</EmptyDescription>}
                </ThemeDescription>
                {item.quotes.length > 0 && (
                  <QuoteList>
                    {item.quotes.slice(0, 2).map((quote, idx) => (
                      <QuoteItem key={idx}>&quot;{quote}&quot;</QuoteItem>
                    ))}
                  </QuoteList>
                )}
              </ThemeRow>
            )) : (
              <EmptyCategory>No themes in this category.</EmptyCategory>
            )}
          </ThemeColumn>

          <ThemeColumn>
            <ThemeColumnHeader $color={SCORE_LOW_COLOR}>
              <ThemeTitleIcon variant="negative">
                <Icon name="warning sign" />
              </ThemeTitleIcon>
              Areas for Improvement
            </ThemeColumnHeader>
            {groupedThemes.negative.length > 0 ? groupedThemes.negative.map((item) => (
              <ThemeRow key={`negative-${item.title}`}>
                <ThemeHeader>
                  <ThemeStatusBar $color={item.statusColor} />
                  <ThemeTitleText>
                    {item.title}
                    {typeof item.count === 'number' ? <ThemeCount>· {item.count} mentions</ThemeCount> : null}
                  </ThemeTitleText>
                </ThemeHeader>
                <ThemeDescription>
                  {item.description || <EmptyDescription>No description available</EmptyDescription>}
                </ThemeDescription>
                {item.quotes.length > 0 && (
                  <QuoteList>
                    {item.quotes.slice(0, 2).map((quote, idx) => (
                      <QuoteItem key={idx}>&quot;{quote}&quot;</QuoteItem>
                    ))}
                  </QuoteList>
                )}
              </ThemeRow>
            )) : (
              <EmptyCategory>No themes in this category.</EmptyCategory>
            )}
          </ThemeColumn>
        </SectionGroup>
      </MobilePageSection>
    </MobilePageContent>
  );
};

export default MobilePillarPage;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const ScoreCircle = styled.div`
  position: relative;
  width: 62px;
  height: 62px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;


const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const SubText = styled.div`
  font-size: 13px;
  color: #475569;
  line-height: 1.4;
`;

const QuestionText = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`;

const DeltaPill = styled.div<{ delta: number }>`
  background: ${({ delta }) => (delta >= 0 ? '#dcfce7' : '#fee2e2')};
  color: ${({ delta }) => (delta >= 0 ? '#166534' : '#991b1b')};
  padding: 8px 14px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
`;

const SectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeading = styled.h3`
  margin: 0 0 0px;
  font-size: 16px;
  font-weight: 550;
  color: #0f172a;
  line-height: 1.2;
  padding-top: 20px;
`;

const SentimentPill = styled.div<{ $background: string; $color: string; $borderColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  max-width: 100%;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${props => props.$color};
  background: ${props => `${props.$background}55`};
  border: 1px solid ${props => `${props.$borderColor}60`};
`;

const ThemeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ThemeColumnHeader = styled.div<{ $color: string }>`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 9999px;
  background: ${props => `${props.$color}15`};
  border: 1px solid ${props => `${props.$color}30`};
`;

const ThemeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0 8px 6px;
`;

const ThemeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ThemeStatusBar = styled.div<{ $color: string }>`
  width: 4px;
  min-height: 22px;
  background: ${props => props.$color};
  border-radius: 9999px;
  flex-shrink: 0;
`;

const ThemeTitleText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
`;

const ThemeCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
`;

const ThemeDescription = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.45;
  margin-left: 12px;
`;

const EmptyDescription = styled.span`
  color: #9ca3af;
`;

const QuoteList = styled.ul`
  margin: 0;
  padding: 0 0 0 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const QuoteItem = styled.li`
  font-size: 12px;
  color: #4b5563;
  font-style: italic;
  line-height: 1.4;
`;

const EmptyCategory = styled.div`
  font-size: 13px;
  color: #6b7280;
  padding-left: 4px;
`;
